import type { Location } from 'react-router-dom';
import { getScanModalBackground } from '../../scan/scanModalNavigation';
import { isCollectionScannerRoute, isDeckEditorRoute } from './routeChrome';

export type MobileTabId = 'cards' | 'collection' | 'decks' | 'play' | 'team';

const STORAGE_KEY = 'riftbound:mobile-tab-paths';

export const MOBILE_TAB_ROOTS: Record<MobileTabId, string> = {
  cards: '/search',
  collection: '/collection',
  decks: '/decks',
  play: '/play',
  team: '/team',
};

/** Which primary tab owns a pathname (scan modal is not a stack tip). */
export function mobileTabIdForPath(pathname: string): MobileTabId | null {
  if (pathname === '/search' || pathname.startsWith('/cards')) return 'cards';
  if (isCollectionScannerRoute(pathname)) return null;
  if (
    pathname === '/collection' ||
    pathname.startsWith('/collection/') ||
    pathname === '/features/collection'
  ) {
    return 'collection';
  }
  if (pathname === '/decks' || pathname.startsWith('/decks/')) return 'decks';
  if (
    pathname === '/play'
    || pathname.startsWith('/play/')
    || pathname.startsWith('/matches')
    || pathname.startsWith('/events')
  ) {
    return 'play';
  }
  if (pathname === '/team' || pathname.startsWith('/teams/') || pathname === '/features/teams') {
    return 'team';
  }
  return null;
}

export function mobileTabIdForRootPath(to: string): MobileTabId | null {
  return mobileTabIdForPath(to);
}

function readStacks(): Partial<Record<MobileTabId, string>> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Partial<Record<MobileTabId, string>>;
  } catch {
    return {};
  }
}

function writeStacks(stacks: Partial<Record<MobileTabId, string>>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stacks));
  } catch {
    // Private mode / quota — stacks are a convenience only.
  }
}

/** Persist the deepest path for the tab that owns this location. */
export function rememberMobileTabPath(location: Location): void {
  const underlay = getScanModalBackground(location);
  const loc = underlay ?? location;
  if (isCollectionScannerRoute(loc.pathname)) return;
  // Focused full-screen editors keep their own chrome; don't clobber the browse tip.
  if (isDeckEditorRoute(loc.pathname)) return;

  const tab = mobileTabIdForPath(loc.pathname);
  if (!tab) return;

  const path = `${loc.pathname}${loc.search}`;
  const stacks = readStacks();
  if (stacks[tab] === path) return;
  stacks[tab] = path;
  writeStacks(stacks);
}

/** Last path for a tab, or its root. */
export function rememberedMobileTabPath(tabId: MobileTabId): string {
  const saved = readStacks()[tabId];
  if (typeof saved === 'string' && saved.startsWith('/')) return saved;
  return MOBILE_TAB_ROOTS[tabId];
}
